import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
    addReduxMarker,
    selectDealer,
    setBAC,
    setCurrentLatLng,
    setGoogleMapsLookupError,
    setReduxMarkers
} from "../../core/redux/ActionCreator";
import {usePrevious} from "../../shared/util/util";
import _ from "lodash";

// variables
const GOOGLE_MAP_API_KEY = 'gme-adamopelag';
// dummy data for testing
const INITIAL_LOCATION = {
    lat: 30.411909,
    lng: -97.663567,
    dealerName: 'GM Innovation Center'
};
const INITIAL_ZOOM = 10;
// styles
const mapStyles = {
    width: '100%',
    height: '400px',
};

const desktopStyles = {
    width: '500px',
    height: '600px',
    position: 'relative',
    overflow: 'hidden',
};

function Map(props) {
    // eslint-disable-next-line
    const [googleMapRef, setGoogleMapRef] = useState(null);
    const googleMap = useRef(null);
    const geocoder = useRef(null);
    const infoWindow = useRef(null);
    const [desktop] = useState(props.desktop);

    const zipcode = useSelector(state => state.yearMakeModelReducer.zipCode);
    const currentPosition = useSelector(state => state.dealerReducer.currentLatLng);
    const markerDictionary = useSelector(state => state.dealerReducer.markers);
    const dealers = useSelector(state => state.dealerReducer.dealers);
    const selectedDealer = useSelector(state => state.dealerReducer.selectedDealer);
    const dispatch = useDispatch();
    const svgMarker = {
        path: "M 12,2 C 8.1340068,2 5,5.1340068 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.8659932 -3.134007,-7 -7,-7 z",
        fillColor: "black",
        fillOpacity: 1,
        strokeWeight: 0,
        rotation: 0,
        scale: 1,
    };

    // Boolean to stop map render with ref is null to prevent map from never rendering
    const [hide, setHide] = useState(false);

    let style = mapStyles;
    if (desktop) {
        style = desktopStyles;
    }
    const labels = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let labelIndex = 0;

    // helper functions
    const createGoogleMap = (node) =>
        new window.google.maps.Map(node, {
            zoom: props.zoom ? props.zoom : INITIAL_ZOOM,
            center: {
                lat: currentPosition ? currentPosition.lat : INITIAL_LOCATION.lat,
                lng: currentPosition ? currentPosition.lng : INITIAL_LOCATION.lng
            }
        });

    const createInfoWindow = () =>
        new window.google.maps.InfoWindow();

    const setMapOnAll = (map) => {
        Object.keys(markerDictionary).forEach((markerId) => {
            markerDictionary[markerId].setMap(map)
        });
    };

    // const showMarkers = () => {
    //     setMapOnAll(googleMap.current);
    // };

    const clearMarkers = () => {
        setMapOnAll(null);
    };

    const deleteMarkers = () => {
        clearMarkers();
        dispatch(setReduxMarkers({}));
        //dispatch(selectDealer({dealerBAC:''}));
        dispatch(setBAC(0));
        infoWindow.current.close();
    };

    const addMarker = (loc) => {
        let metersToDealer
        let milesToDealer
        if(currentPosition != null) {
             metersToDealer = window.google.maps.geometry.spherical.computeDistanceBetween(currentPosition, loc);
             milesToDealer = Number.parseFloat(metersToDealer * 0.000621).toFixed(2);
        }
        else{milesToDealer = ''}

        const marker = new window.google.maps.Marker({
            position: loc,
            map: googleMap.current,
            title: loc.dealerName ? loc.dealerName : '',
            markerId: loc.bac,
            label: labels[labelIndex++ % labels.length],
            DistanceToDealer: milesToDealer
        });

        marker.addListener("click", () => {
            infoWindow.current.close();
            infoWindow.current.setContent(marker.getTitle());
            infoWindow.current.open(marker.getMap(), marker);
            dispatch(selectDealer({dealerBAC:loc.bac}));
            dispatch(setBAC(loc.bac));
        });
        if (selectedDealer === loc.bac) {
            infoWindow.current.close();
            infoWindow.current.setContent(marker.getTitle());
            infoWindow.current.open(marker.getMap(), marker);
        }
        dispatch(addReduxMarker(loc.bac, marker));
    };

    const createGeocoder = () =>
        new window.google.maps.Geocoder();

    const getGeocoder = (zip) => {
        geocoder.current.geocode({'address': zip}, (results, status) => {
            switch (status) {
                case window.google.maps.GeocoderStatus.OK:
                    const coords = {
                        lat: results[0].geometry.location.lat(),
                        lng: results[0].geometry.location.lng(),
                    };
                    dispatch(setCurrentLatLng(coords));
                    dispatch(setGoogleMapsLookupError(''));
                    break;
                case window.google.maps.GeocoderStatus.ZERO_RESULTS:
                    dispatch(setCurrentLatLng({lat: 0, lng: 0}));
                    //dispatch(setGoogleMapsLookupError('No coordinates found!'));
                    break;
                default:
                    dispatch(setCurrentLatLng({lat: 0, lng: 0}));
                    dispatch(setGoogleMapsLookupError(status));
                    break;
            }
        });
    };

    const setMapCenter = () => {
        if (currentPosition) {
            googleMap.current.setCenter(currentPosition);
        }
    };

    const addDealerMarkers = () => {
        if (dealers) {
            dealers.forEach(dealer => {
                addMarker({
                    lat: dealer.latitude,
                    lng: dealer.longitude,
                    dealerName: dealer.dealerName,
                    bac: dealer.baccode,
                });
            })
            googleMapMarker();
        }
    };

    const googleMapMarker = () => {

        const marker = new window.google.maps.Marker({
            position: currentPosition,
            map: googleMap.current,
            title: 'My Location',
            markerId: '0',
            icon: svgMarker,
        });
        marker.addListener("click", () => {
            infoWindow.current.close();
            infoWindow.current.setContent(marker.getTitle());
            infoWindow.current.open(marker.getMap(), marker);
        });

        dispatch(addReduxMarker(marker.markerId, marker));
    };

    const removeAndCreateDealersMarkers = () => {
        deleteMarkers();
        addDealerMarkers();
    };

    // useEffect Hook
    const onGoogleMapRefChange = useCallback((node) => {
        const initMap = () => {
            googleMap.current = createGoogleMap(node);
            geocoder.current = createGeocoder();
            infoWindow.current = createInfoWindow();
            if(zipcode && zipcode !== "empty"){
                getGeocoder(zipcode);
            }else{
                setMapCenter();
            }
            if (Object.keys(markerDictionary).length > 0) {
                deleteMarkers();
                infoWindow.current.close();
            }
        };

        setGoogleMapRef(node);
        if (node === null) {
            // node is null, if DOM node of ref had been unmounted before
            setHide(true);
        } else {
            // ref value exists
            setHide(false);
            if (!window.google) {
                const googleMapScript = document.createElement('script');
                googleMapScript.src = `https://maps.googleapis.com/maps/api/js?client=${GOOGLE_MAP_API_KEY}&libraries=geometry&v=quarterly`;
                window.document.body.appendChild(googleMapScript);

                googleMapScript.addEventListener('load', initMap);
            } else {
                initMap();
            }
        }

        return function cleanup() {
            if (infoWindow.current) {
                infoWindow.current.close();
            }
            deleteMarkers();
        }
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        if (zipcode && zipcode !== "empty" && geocoder.current && googleMap.current) {
            getGeocoder(zipcode);
        }
        // eslint-disable-next-line
    }, [zipcode]);
    const prevCurrentPosition = usePrevious(currentPosition);
    useEffect(() => {
        if (!_.isEqual(prevCurrentPosition, currentPosition) && googleMap.current) {
            setMapCenter();
        }
        // eslint-disable-next-line
    }, [currentPosition]);
    useEffect(() => {
        if (dealers && googleMap.current) {
            removeAndCreateDealersMarkers();
        }
        // eslint-disable-next-line
    }, [dealers]);
    // const prevMarkerDictionary = usePrevious(markerDictionary);
    // useEffect(() => {
    //     if (!_.isEqual(prevMarkerDictionary, markerDictionary) && googleMap.current) {
    //         showMarkers();
    //     }
    //     // eslint-disable-next-line
    // }, [markerDictionary]);

    return (
        <>
            {!hide && <div
                id={"google-map"}
                ref={onGoogleMapRefChange}
                style={style}
            />}
        </>
    )
}

export default Map
